import React, { Suspense, startTransition } from 'react';
import loadable from "@loadable/component";
import { Routes, Route, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import HealthCheckRoute from './routes/HealthCheckRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import I18n from './utils/I18n/I18n';
import './App.scss';
import PrivateRoute from "./utils/privateRoute";
import CookieDeclaration from "./screens/CookieDeclaration";
const GlobalProviders = loadable(() => import('./globalProviders'));
import ServiceWorkerRegistration from './utils/registerServiceWorker';
import { useHydrationDelay } from './hooks/useHydrationDelay';

const DynamicRoute = loadable(() => import('./routes/DynamicRoute'), {ssr: true});


// Komponenty renderowane po stronie serwera (krytyczne)
const Header = loadable(() => import('./components/Header/Header'), {ssr: true});
const HeadMetaTags = loadable(() => import('./components/HeadMetaTags'));
const PreConnect = loadable(() => import('./components/Header/PreConnect'));
const Footer = loadable(() => import('./components/Footer/Footer'), {ssr: true});

// Komponenty renderowane tylko po stronie klienta
const ScrollToTop = loadable(() => import('./components/ScrollToTop'));
const NewsletterSlider = loadable(() => import('./components/NewsletterSlider/NewsletterSlider'));
const PageTransitionWrapper = loadable(() => import('./components/PageTransitionWrapper'),{ssr: true});
const Media = loadable(() => import('./screens/Media'));
const CompareBlock = loadable(() => import('./components/Compare/CompareBlock'),{ssr:false});
const Compare = loadable(() => import('./components/Compare/Compare'),{ssr: false});
const SearchResults = loadable(() => import('./screens/SearchResults/SearchResults'));
const CheckoutPaymentSuccess = loadable(() => import('./screens/Checkout/CheckoutPaymentSuccess'));
const CheckoutPaymentCancel = loadable(() => import('./screens/Checkout/CheckoutPaymentCancel'));
const NotFoundPage = loadable(() => import('./routes/NotFoundPage/NotFoundPage'));
const SalesAndDiscounts = loadable(() => import('./screens/SalesAndDiscounts/SalesAndDiscounts'));
const Blog = loadable(() => import('./screens/Blog/Blog'));
const BlogDetails = loadable(() => import('./screens/Blog/BlogDetails/BlogDetails'));
const News = loadable(() => import('./screens/News/News'));
const NewsDetails = loadable(() => import('./screens/News/NewsDetails/NewsDetails'));
const Cart = loadable(() => import('./screens/Cart/Cart'),{ssr: false});
const Checkout = loadable(() => import('./screens/Checkout/Checkout'),{ssr: false});
const CheckoutSuccess = loadable(() => import('./screens/CheckoutSuccess'),{ssr: false});
const Registration = loadable(() => import('./screens/Registration/Registration'));
const Login = loadable(() => import('./screens/Login/Login'));
const RequestReset = loadable(() => import('./screens/Login/PasswordReset/RequestReset'));
const ResetPassword = loadable(() => import('./screens/Login/PasswordReset/ResetPassword'));
const LogoutSuccess = loadable(() => import('./screens/Logout/LogoutSuccess'));
const SubscriberConfirm = loadable(() => import('./screens/Newsletter/SubscriberConfirm'));
const PersonalArea = loadable(() => import('./screens/PersonalAreaScreen/PersonalArea'));
const Delete = loadable(() => import('./screens/Login/Delete'));
const ProductAlertSubscribe = loadable(() => import('./components/ProductAlert/ProductAlertSubscribe'));
const ProductAlertUnsubscribe = loadable(() => import('./components/ProductAlert/ProductAlertUnsubscribe'));
const ProductAlertUnsubscribeAll = loadable(() => import('./components/ProductAlert/ProductAlertUnsubscribeAll'));
const Wishlist = loadable(() => import('./components/Wishlist/Wishlist'));


const AppContent = ({ configurableProps }) => {
    const { pathname } = useLocation();
    const configuredProps = configurableProps;

    const showHeaderFooter = (!pathname.includes('checkout') || pathname.includes('success')) && !configuredProps;
    const showNewsletterBanner = !pathname.includes('account') && !pathname.includes('checkout');

    return (
        <>
            {showHeaderFooter && <Header />}
            <HeadMetaTags />
            <PreConnect />
            <div style={{ margin: '0 auto', padding: '0 15px' }}>
                {showNewsletterBanner && <NewsletterSlider />}
                <ScrollToTop />
                <ErrorBoundary>
                    <I18n>
                        {(t) => (
                            <PageTransitionWrapper showLoader={false}>
                                {/* Używamy tego samego renderowania dla serwera i klienta, aby zapobiec błędom hydratacji */}
                                <Suspense fallback={
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                        <div>Loading...</div>
                                    </div>
                                }>
                                    <Routes>
                                        <Route path="/cookies" element={<CookieDeclaration />} />
                                        <Route path="/image/media/*" element={<Media />} />
                                        <Route path="/media/*" element={<Media />} />
                                        <Route path="/compare" element={<Compare/>}/>
                                        <Route path="/catalogsearch/result" element={<SearchResults />} />
                                        <Route path="/checkout/payment/success" element={<CheckoutPaymentSuccess />} />
                                        <Route path="/checkout/payment/cancel" element={<CheckoutPaymentCancel />} />
                                        <Route path="/404" element={<NotFoundPage />} />
                                        <Route path="/health-check" element={<HealthCheckRoute />} />
                                        <Route path="/akcije-i-popusti.html" element={<SalesAndDiscounts />} />
                                        <Route path="/blog/" element={<Blog />} />
                                        <Route path="/blog/tag/:tagId" element={<Blog />} />
                                        <Route path="/blog/:blogId" element={<BlogDetails />} />
                                        <Route path={`/${t('news.newsLink')}/`} element={<News />} />
                                        <Route path={`/${t('news.newsLink')}/:newsId/`} element={<NewsDetails />} />
                                        <Route path="/checkout/cart/" element={<Cart />} />
                                        <Route path="/checkout/success" element={<CheckoutSuccess />} />
                                        <Route path="/checkout/" element={<Checkout />} />



                                        <Route path="/customer/registration/" element={<Registration />} />
                                        <Route path="/customer/login" element={<Login />} />
                                        <Route path="/customer/delete" element={<Delete />} />
                                        <Route path="/customer/account/forgotpassword/" element={<RequestReset />} />
                                        <Route path="/customer/account/createpassword/" element={<ResetPassword />} />
                                        <Route
                                            path="/productalert/add/:productId/:productUrl"
                                            element={<ProductAlertSubscribe />}
                                        />
                                        <Route
                                            path="/productalert/unsubscribe/stock/product/:productId/"
                                            element={<ProductAlertUnsubscribe />}
                                        />
                                        <Route
                                            path="/productalert/unsubscribe/stockAll/"
                                            element={<ProductAlertUnsubscribeAll />}
                                        />
                                        <Route path="/customer/account/logoutSuccess/" element={<LogoutSuccess />} />
                                        <Route
                                            path="/newsletter/subscriber/confirm/id/:confirmId/code/:confirmCode"
                                            element={<SubscriberConfirm />}
                                        />
                                        <Route path="/wishlist/:sharingCode" element={<Wishlist />} />
                                        {/* PrivateRoute wymaga dostosowania do v6 */}
                                        <Route
                                            path="/customer/account/*"
                                            element={
                                                <PrivateRoute>
                                                    <PersonalArea />
                                                </PrivateRoute>
                                            }
                                        />
                                        <Route path="*" element={<DynamicRoute  />} />
                                    </Routes>
                                </Suspense>
                            </PageTransitionWrapper>
                        )}
                    </I18n>
                </ErrorBoundary>
            </div>
            {showHeaderFooter && <Footer />}
            <CompareBlock/>
        </>
    );
};

function App({ configurableProps }) {
    const { pathname } = useLocation();
    const isHealthCheck = pathname.includes('health-check');

    if (isHealthCheck) {
        return <HealthCheckRoute/>;
    }


    return (
        <>
            <ServiceWorkerRegistration/>
            <GlobalProviders>
                <AppContent configurableProps={configurableProps}/>
            </GlobalProviders>
        </>
    );
}

App.propTypes = {
    configurableProps: PropTypes.object,
};

export default App;